import { Box, Card, Typography } from '@material-ui/core';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  BooleanInput,
  maxLength,
  required,
  SelectInput,
  TextInput,
  useDataProvider,
} from 'react-admin';
import { Prize, PrizeCategory, PrizeSubcategory } from '../../types/prize';
import RichTextInput from 'ra-input-rich-text';
import ImagesField, { ImagesTypeEnum } from '../common/imagesField/ImagesField';
import {
  aboutTextAreaValidate,
  aboutTextAreaValidateNotRequired,
} from '../../helpers/inputsValidate';
import DateTimePicker from 'react-datetime-picker';
import { OverviewContainer } from '../dreamHome/shared/overview/OverviewContainer';
import { Overview } from '../dreamHome/shared/overview/OverviewBlock';

interface CategoryOption {
  name: string;
  id: string;
  isActive: boolean;
}
interface Props {
  record: Prize | undefined;
  isShow: boolean;
  imagesError: boolean;
  isPrize: boolean;
  setCountdown?: (name: string, value: string) => void;
  countdownError?: boolean;
  countdownStartDate?: Date | string;
  countdownEndDate?: Date | string;
  properties?: Array<Overview>;
  setProperties?: Dispatch<SetStateAction<Overview[]>>;
  overviewError?: boolean;
}

const getChoses = (data: Array<PrizeCategory>): Array<CategoryOption> => {
  return data.reduce((acc, item) => {
    const optionItem: CategoryOption = {
      name: item.title,
      id: item.id,
      isActive: item.isActive,
    };
    acc.push(optionItem);
    return acc;
  }, Array<CategoryOption>());
};

const PrizeGeneralForm: FC<Props> = (props) => {
  const validateRequired = [required(), maxLength(50)];
  const validateRequiredDescription = [required(), maxLength(200)];
  const [aboutCounter, setAboutCounter] = useState(0);
  const [state, setState] = useState({
    categories: Array<PrizeCategory>(),
    subCategories: Array<PrizeSubcategory>(),
    startDateError: false,
    endDateError: false,
  });
  const dataProvider = useDataProvider();

  const removeTags = (str: string): string => {
    const newStr = str
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return newStr;
  };

  useEffect(() => {
    if (props.record) {
      if (Object.keys(props.record).length !== 0) {
        if (props.isPrize) {
          const categoryId =
            typeof props.record.prizeCategory === 'string'
              ? props.record.prizeCategory
              : '';
          getCategory(props.record.categoryid || categoryId);
        }
        setAboutCounter(removeTags(props.record.description || '').length);
        return;
      }
    }
    getCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCategory = async (categoryId?: string) => {
    let getSub = Array<PrizeSubcategory>();
    const category = (await dataProvider.getList('prizeCategories', {
      filter: { isActive: true },
      pagination: { page: 0, perPage: 0 },
      sort: { field: 'id', order: 'ASC' },
    })) as any;
    if (categoryId) {
      const result = await getSubCategory(categoryId || category.data[0]._id);
      getSub = result.data;
    }
    setState({ ...state, categories: category.data, subCategories: getSub });
  };

  const getSubCategory = async (selectedCategory: string) => {
    const categories = Array<string>();
    categories.push(selectedCategory);
    return (await dataProvider.getList(`subcategories`, {
      filter: { categoriesIds: categories },
      pagination: { page: 0, perPage: 0 },
      sort: { field: 'id', order: 'ASC' },
    })) as any;
  };

  const handleAbout = (e: string) => {
    e = removeTags(e);
    const count = e.length === 0 ? 0 : e.length;
    setAboutCounter(count);
  };

  const handleCategorySelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const subCategories = await getSubCategory(event.target.value);
    setState({ ...state, subCategories: subCategories.data });
  };

  const categoryChoices = getChoses(state.categories);
  const subCategoriesChoices = getChoses(state.subCategories);

  const CategoryOptions = ({ record }) => {
    return (
      <span className={record.isActive ? '' : 'inactive-category'}>
        {record.name}{' '}
      </span>
    );
  };

  const SubCategoryOptions = ({ record }) => {
    return (
      <span className={record.isActive ? '' : 'inactive-category'}>
        {record.name}{' '}
      </span>
    );
  };

  const setEndDate = (value: Date | any) => {
    if (value) {
      let error = false;
      const startDate = new Date(props.countdownStartDate!).getTime();
      const endDate = value.getTime();
      if (startDate >= endDate) {
        error = true;
      }
      if (props.setCountdown) {
        props.setCountdown('endDateValue', value.toString());
      }
      setState({
        ...state,
        endDateError: error,
        startDateError: false,
      });
    }
  };

  const setStartDate = (value: Date | any) => {
    if (value) {
      let error = false;
      const endDate = new Date(props.countdownEndDate!).getTime();
      const startDate = value.getTime();
      if (startDate >= endDate) {
        error = true;
      }
      if (props.setCountdown) {
        props.setCountdown('startDateValue', value.toString());
      }
      setState({
        ...state,
        startDateError: error,
        endDateError: false,
      });
    }
  };

  return (
    <>
      <Box display={{ md: 'block', lg: 'flex' }} justifyContent='space-between'>
        <Box width={{ md: '100%', lg: '70%' }} marginTop='2em'>
          <Card>
            <Box padding='2em'>
              {/* <ImagesField
                error={props.imagesError}
                title='Add images for desktop'
                awsFolder='prizePhotos'
                imagesKey={
                  props.record && props.record.photoUrlKeys
                    ? props.record.photoUrlKeys
                    : undefined
                }
              />
              <ImagesField
                error={props.imagesError}
                title='Add images for mobile'
                imagesType={ImagesTypeEnum.MOBILE}
                awsFolder='prizePhotos'
                imagesKey={
                  props.record && props.record.photoUrlKeysMobile
                    ? props.record.photoUrlKeysMobile
                    : undefined
                }
              /> */}
              <Typography variant='h6' gutterBottom className='base-color'>
                Title
              </Typography>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='flex-start'
              >
                <TextInput
                  className='input-form'
                  disabled={props.isShow}
                  source='title'
                  label='Add a title'
                  validate={validateRequired}
                />
                <Box marginLeft='4em'>
                  <BooleanInput source='active' />
                </Box>
              </Box>
              <Box
                display='flex'
                position='relative'
                className='about-section text-parent'
              >
                <RichTextInput
                  onChange={handleAbout}
                  className='rich-text'
                  source='description'
                  label='About'
                  disabled={props.isShow}
                  data={1000}
                  validate={aboutTextAreaValidateNotRequired}
                />
                <div
                  className={
                    aboutCounter === 0
                      ? 'text-placeholder'
                      : 'text-placeholder-fill'
                  }
                >
                  Add a description
                </div>
                <div className='about-text-counter'>{aboutCounter}/1000</div>
              </Box>
            </Box>
          </Card>
        </Box>
        <Box
          width={{ md: '100%', lg: '28%' }}
          marginTop={props.isPrize ? '2em' : '0em'}
        >
          <Box marginTop='2em'>
            {props.setProperties && props.properties && (
              <Box marginTop='2em'>
                <Card>
                  <OverviewContainer
                    properties={props.properties || []}
                    setProperties={props.setProperties}
                    overviewError={!!props.overviewError}
                  />
                </Card>
              </Box>
            )}
            {!props.isPrize && (
              <Box marginTop='2em'>
                <Card>
                  <Box padding='2em'>
                    <Typography
                      variant='h6'
                      gutterBottom
                      className='base-color'
                    >
                      CountDown
                    </Typography>
                    <hr />
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='flex-start'
                    >
                      <Typography gutterBottom className='base-color'>
                        Start date
                      </Typography>
                      <Box>
                        <DateTimePicker
                          value={new Date(props.countdownStartDate!)}
                          format={'dd-MM-y h:mm:ss a'}
                          onChange={setStartDate}
                          maxDate={new Date('2051-01-18T19:55:00.000+00:00')}
                          minDate={new Date('2000-01-18T19:55:00.000+00:00')}
                          disableClock
                        />
                        <Box position='absolute'>
                          {state.startDateError && (
                            <div className='date-error'>
                              Start date cannot be later than finish date
                            </div>
                          )}
                        </Box>
                        {!props.countdownStartDate && props.countdownError && (
                          <div className='validate-address-error'>Required</div>
                        )}
                      </Box>
                    </Box>
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='flex-start'
                      marginTop='2em'
                    >
                      <Typography gutterBottom className='base-color'>
                        Finish date
                      </Typography>
                      <Box>
                        <DateTimePicker
                          value={new Date(props.countdownEndDate!)}
                          format={'dd-MM-y h:mm:ss a'}
                          onChange={setEndDate}
                          maxDate={new Date('2051-01-18T19:55:00.000+00:00')}
                          minDate={new Date('2000-01-18T19:55:00.000+00:00')}
                          disableClock
                        />
                        <Box position='absolute'>
                          {state.endDateError && (
                            <div className='date-error'>
                              Finish date cannot be earlier than Start date
                            </div>
                          )}
                        </Box>
                        {!props.countdownEndDate && props.countdownError && (
                          <div className='validate-address-error'>Required</div>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
            )}
            {/* <Box marginTop='2em'>
              <Card>
                <Box padding='2em'>
                  <Typography variant='h6' gutterBottom className='base-color'>
                    Meta Tags
                  </Typography>
                  <hr />
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='flex-start'
                  >
                    <Typography gutterBottom className='base-color'>
                      Meta Title
                    </Typography>
                    <TextInput
                      className='input-form'
                      disabled={props.isShow}
                      source='metaTitle'
                      label='Add a metaTitle'
                      validate={validateRequiredDescription}
                    />
                  </Box>

                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='flex-start'
                  >
                    <Typography gutterBottom className='base-color'>
                      Meta Description
                    </Typography>
                    <TextInput
                      className='input-form'
                      disabled={props.isShow}
                      source='metaDescription'
                      label='Add a metaDescription'
                      validate={validateRequiredDescription}
                    />
                  </Box>
                </Box>
              </Card>
            </Box> */}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PrizeGeneralForm;
